import React, { useState, useEffect } from "react";
import Hummingbird from "../images/corner/corner-hummingbird.webp";
import { renderStrapiContent } from "../utils/contentfulOptions";
import Fuse from "fuse.js";
import Button from "../components/Button";
import FullPageLoader from "../components/FullPageLoader";

const FAQ_CATEGORIES = [
  {
    label: "Visit",
    keywords: ["hour", "location", "hotel", "stay", "find", "glass"],
  },
  {
    label: "Reservations",
    keywords: [
      "reservation",
      "reserve",
      "booking",
      "tasting",
      "tour",
      "fire pit",
    ],
  },
  {
    label: "Wine",
    keywords: ["wine", "club", "ship", "gift card", "spirit animal"],
  },
  {
    label: "Food",
    keywords: ["food", "gluten", "beverage"],
  },
  {
    label: "Events",
    keywords: ["entertainment", "music", "live"],
  },
  {
    label: "Family & Pets",
    keywords: ["child", "children", "kid", "dog", "pet"],
  },
  {
    label: "Policies",
    keywords: [
      "child",
      "children",
      "kid",
      "dog",
      "pet",
      "smoke",
      "gratuity",
      "outside",
    ],
  },
];

const getTextFromContent = (content) => {
  if (!content) return "";
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content.map(getTextFromContent).join(" ");
  }
  if (typeof content === "object") {
    return [
      content.text,
      content.value,
      getTextFromContent(content.children),
      getTextFromContent(content.content),
    ]
      .filter(Boolean)
      .join(" ");
  }
  return "";
};

const getTags = (question) => {
  const searchableQuestion = question.toLowerCase();
  const tags = FAQ_CATEGORIES.filter(({ keywords }) =>
    keywords.some((keyword) => searchableQuestion.includes(keyword))
  ).map(({ label }) => label);

  return tags.length ? tags : ["General"];
};

const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const FAQ = (props) => {
  useEffect(() => {
    document.title = "Auburn Road - FAQ";
    return () => {
      document.title = "Auburn Road Vineyards";
    };
  }, []);
  const [searchString, setSearchString] = useState("");
  const [activeTag, setActiveTag] = useState("All");

  // null/undefined means the Strapi request is still in flight
  if (props.data === null || props.data === undefined) {
    return <FullPageLoader />;
  }

  const openContact = () => {
    if (props.toggleContactOverlay) {
      props.toggleContactOverlay();
    }
  };
  const FaqBlock = ({ question, answer, tags, defaultOpen }) => {
    return (
      <li className="faq-card">
        <details className="faq-card__details" open={defaultOpen}>
          <summary className="faq-card__summary">
            <span>
              <span className="faq-card__tags">
                {tags.map((tag) => (
                  <span className="faq-card__tag" key={tag}>
                    {tag}
                  </span>
                ))}
              </span>
              <h3 className="question">{question}</h3>
            </span>
            <span className="faq-card__icon" aria-hidden="true" />
          </summary>
          <div className="answer">{answer}</div>
        </details>
      </li>
    );
  };
  // Handle FAQ data - could be props.data or passed directly as an array
  const faqData = props.data || props;

  const reduced = (Array.isArray(faqData) ? faqData : []).reduce(
    (accum, qa) => {
      const question =
        qa.attributes?.question || qa.fields?.question || qa.question;
      const answer =
        qa.attributes?.answers ||
        qa.attributes?.answer ||
        qa.fields?.answer ||
        qa.answers ||
        qa.answer;

      if (!question) return accum;

      return [
        ...accum,
        {
          question,
          answer,
          answerText: getTextFromContent(answer),
          tags: getTags(question),
        },
      ];
    },
    []
  );
  const availableTags = [
    "All",
    ...FAQ_CATEGORIES.map(({ label }) => label).filter((tag) =>
      reduced.some((faq) => faq.tags.includes(tag))
    ),
    ...(reduced.some((faq) => faq.tags.includes("General")) ? ["General"] : []),
  ];
  const fuse = new Fuse(reduced, {
    threshold: 0.35,
    ignoreLocation: true,
    keys: ["question", "answerText", "tags"],
  });

  const searchedFaqs = searchString
    ? fuse.search(searchString).map((x) => x.item)
    : reduced;
  const faqs =
    activeTag === "All"
      ? searchedFaqs
      : searchedFaqs.filter((faq) => faq.tags.includes(activeTag));
  const resultLabel = `${faqs.length} ${
    faqs.length === 1 ? "answer" : "answers"
  }`;

  return (
    <>
      <img className="corner-hummingbird" src={Hummingbird} alt="" />
      <div className="container faq-page p-t-100">
        <section className="faq-hero">
          <div>
            <p className="faq-hero__kicker">Auburn Road Vineyards</p>
            <h1 className="huge-text-no-quote mb-3">FAQ's</h1>
            <p className="faq-hero__intro">
              Find quick answers about visiting the winery, reservations, wine,
              food, events, and house policies.
            </p>
          </div>
          <div className="faq-hero__card">
            <span className="faq-hero__number mb-2">{reduced.length}</span>
            <span>questions answered</span>
          </div>
        </section>

        <section className="faq-controls" aria-label="FAQ filters">
          <label className="faq-search" htmlFor="faq-search">
            <span>Search the FAQ</span>
            <input
              id="faq-search"
              className="email-input"
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              placeholder="type your question here"
            />
          </label>

          <div className="faq-category-list" aria-label="Filter by topic">
            {availableTags.map((tag) => (
              <button
                className={`faq-category ${activeTag === tag ? "active" : ""}`}
                key={tag}
                onClick={() => setActiveTag(tag)}
                type="button"
              >
                {tag}
              </button>
            ))}
          </div>
        </section>

        <div className="faq-results-bar">
          <p>
            Showing <strong>{resultLabel}</strong>
            {activeTag !== "All" && ` tagged ${activeTag}`}
            {searchString && ` for "${searchString}"`}
          </p>
          {(searchString || activeTag !== "All") && (
            <button
              className="plain-button"
              onClick={() => {
                setSearchString("");
                setActiveTag("All");
              }}
              type="button"
            >
              Clear filters
            </button>
          )}
        </div>

        <ul className="ul faq-list">
          {faqs.length ? (
            faqs.map((x, i) => {
              return (
                <FaqBlock
                  key={`${slugify(x.question)}-${i}`}
                  question={x.question}
                  answer={renderStrapiContent(x.answer)}
                  tags={x.tags}
                  defaultOpen={Boolean(searchString) || i === 0}
                />
              );
            })
          ) : (
            <li className="faq-empty">
              <h3>No matching answers yet.</h3>
              <p>Your search did not return any results.</p>
              <button
                className="brush-button button"
                onClick={() => {
                  setSearchString("");
                  setActiveTag("All");
                }}
                type="button"
              >
                Show all FAQs
              </button>
              <p className="faq-empty__contact">
                Still stuck?{" "}
                <button
                  className="faq-contact-link"
                  onClick={openContact}
                  type="button"
                >
                  Reach out to us here
                </button>
                .
              </p>
            </li>
          )}
        </ul>

        <aside className="faq-contact">
          <h2 className="faq-contact__heading">Can't find the answer?</h2>
          <p className="faq-contact__text">
            Reach out to us here and we'll be happy to help.
          </p>
          <Button type="secondary" onClick={openContact}>
            Contact us
          </Button>
        </aside>
      </div>
    </>
  );
};
export default FAQ;
